import * as React from 'react';

import {
  TextField as AriaTextField,
  TextFieldProps as AriaTextFieldProps,
  ValidationResult as AriaValidationResult,
  composeRenderProps,
} from 'react-aria-components';
import PhoneInput, { Country } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

import { Maybe, cn, maybe } from '../../../lib';
import { FieldError, FieldGroup, Label } from '../Field';

/**
 * PhoneField props interface.
 * Extends AriaTextFieldProps and adds phone-specific props.
 */
export type PhoneFieldProps = Omit<AriaTextFieldProps, 'value' | 'onChange'> & {
  /** Label text displayed above the input */
  label?: string;
  /** Inner label text displayed inside the input field */
  innerLabel?: string;
  /** Description text displayed below the label */
  description?: string;
  /** Error message shown when validation fails. Can be a string or a function that returns a string based on validation results */
  errorMessage?: string | ((validation: AriaValidationResult) => string);
  /** Placeholder to be set in the input. */
  placeholder?: string;
  /** Content to be rendered to the left of the input */
  prefix?: React.ReactNode;
  /** Content to be rendered to the right of the input */
  suffix?: React.ReactNode;
  /** Phone number value */
  value?: Maybe<string>;
  /** Phone number change handler */
  onChange?: (value: string | undefined) => void;
  /** Default country code */
  defaultCountry?: Country;
  /** Countries to display in the country selector */
  countries?: Country[];
  /** Additional props for the PhoneInput component */
  phoneInputProps?: React.ComponentProps<typeof PhoneInput>;
};

export const PhoneField = ({
  className,
  label,
  innerLabel,
  description,
  errorMessage,
  placeholder,
  prefix,
  suffix,
  value,
  onChange,
  defaultCountry = 'US',
  countries,
  phoneInputProps,
  ref,
  ...props
}: Omit<PhoneFieldProps, 'className'> & {
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
}) => {
  const phoneInputContainerRef = React.useRef<HTMLDivElement>(null);

  const [currentFocus, setCurrentFocus] = React.useState<
    'flag' | 'number' | undefined
  >();

  React.useLayoutEffect(() => {
    const handleFocusIn = () => {
      if (document.activeElement?.tagName === 'SELECT') {
        setCurrentFocus('flag');
      } else {
        setCurrentFocus('number');
      }
    };
    const handleFocusOut = () => {
      setCurrentFocus(undefined);
    };

    phoneInputContainerRef.current?.addEventListener('focusin', handleFocusIn);
    phoneInputContainerRef.current?.addEventListener(
      'focusout',
      handleFocusOut,
    );

    return () => {
      phoneInputContainerRef.current?.removeEventListener(
        'focusin',
        handleFocusIn,
      );

      phoneInputContainerRef.current?.removeEventListener(
        'focusout',
        handleFocusOut,
      );
    };
  }, []);

  return (
    <AriaTextField
      aria-label={'Phone field'}
      className={composeRenderProps(className, (className) =>
        cn('@PhoneField', 'group flex flex-col gap-2', className),
      )}
      {...props}
      value={maybe(value)}
    >
      {label && (
        <div className="flex flex-row items-center gap-2">
          {label && <Label aria-required={props.isRequired}>{label}</Label>}
        </div>
      )}
      <FieldGroup
        ref={ref}
        className={cn(
          'relative pl-2',
          innerLabel && 'h-auto p-0',
          '[&_.PhoneInput]:w-full',
          '[&_.PhoneInputInput]:w-full [&_.PhoneInputInput]:border-0 [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:pl-1 [&_.PhoneInputInput]:text-sm [&_.PhoneInputInput]:ring-0 [&_.PhoneInputInput]:outline-none',
          '[&_.PhoneInputCountryIcon]:mr-1',
          '[&_.PhoneInputCountryIconImg]:rounded-[1px]',
          '[&_.PhoneInputCountryIcon]:rounded-[1px]',
          currentFocus === 'flag' &&
            '[&_.PhoneInputCountryIcon]:shadow-none [&_.PhoneInputCountryIcon]:outline-4 [&_.PhoneInputCountryIcon]:outline-primary/40',
          className,
        )}
        isDisabled={props.isDisabled}
        innerLabel={innerLabel}
        prefix={prefix}
        suffix={suffix}
      >
        <div ref={phoneInputContainerRef} className="contents">
          <PhoneInput
            placeholder={placeholder}
            value={value ?? undefined}
            onChange={onChange ?? (() => void 0)}
            defaultCountry={defaultCountry}
            countries={countries}
            international
            countryCallingCodeEditable={false}
            {...phoneInputProps}
          />
        </div>
      </FieldGroup>
      {description && (
        <div className="text-sm text-neutral/40">{description}</div>
      )}
      {errorMessage && <FieldError>{errorMessage}</FieldError>}
    </AriaTextField>
  );
};
